import { NavigationMenuLink } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import Link from "next/link";
import type { ProductsPanel } from "../../data";
import ViewAllLink from "../viewAllLink/viewAllLink.component";
import IconTile from "@/components/common/iconTile/iconTile.component";

interface ProductsMenuPanelProps {
  panel: ProductsPanel;
  pathname: string;
}

// Classes go on NavigationMenuLink (not the inner <Link>) so cn() can
// override shadcn's defaults (flex-col, p-2, grey hover) - with asChild the
// Slot only concatenates the child's className, it doesn't merge it.
const activeOverrides =
  "data-[active=true]:bg-paper data-[active=true]:hover:bg-paper data-[active=true]:focus:bg-paper";

// Desktop Products mega menu: product areas on the left, loyalty program
// types on the right, with the "customized solution" promo under the grid.
const ProductsMenuPanel = ({ panel, pathname }: ProductsMenuPanelProps) => (
  <div className="grid grid-cols-[5fr_7fr]">
    <div className="p-5 pr-4">
      <span className="mb-3 ml-3 block text-sm font-semibold text-ink-muted">
        {panel.sectionLabel}
      </span>
      <ul className="grid gap-0.5">
        {panel.links.map((link, index) => {
          const isCurrent = pathname === link.route;
          return (
            <li key={link.route}>
              <NavigationMenuLink
                asChild
                active={isCurrent}
                className={cn(
                  "grid grid-cols-[44px_1fr] items-center gap-3.5 rounded-xl px-3 py-2.5 text-ink hover:bg-paper hover:text-ink focus:bg-paper focus:text-ink",
                  activeOverrides,
                )}
              >
                <Link
                  href={link.route}
                  aria-current={isCurrent ? "page" : undefined}
                >
                  {link.icon && <IconTile icon={link.icon} index={index} />}
                  <span>
                    <span
                      className={cn(
                        "block text-base leading-tight font-semibold",
                        isCurrent && "text-primary",
                      )}
                    >
                      {link.label}
                    </span>
                    <span className="mt-0.5 block text-[13.5px] leading-snug text-ink-muted">
                      {link.description}
                    </span>
                  </span>
                </Link>
              </NavigationMenuLink>
            </li>
          );
        })}
      </ul>
      {panel.allLink && (
        <ViewAllLink link={panel.allLink} isMenuLink className="mt-2" />
      )}
    </div>

    <div className="border-l border-line px-7 pt-5 pb-6">
      <span className="mb-3 ml-3 block text-sm font-semibold text-ink-muted">
        {panel.gridLabel}
      </span>
      <ul className="grid grid-cols-2 gap-x-2 gap-y-1 xl:grid-cols-3">
        {panel.gridLinks.map((link, index) => {
          const isCurrent = pathname === link.route;
          return (
            <li key={link.route}>
              <NavigationMenuLink
                asChild
                active={isCurrent}
                className={cn(
                  "flex-row items-center gap-3 rounded-xl px-2.5 py-2 text-base leading-tight font-medium text-ink hover:bg-paper hover:text-ink focus:bg-paper focus:text-ink data-[active=true]:font-semibold data-[active=true]:text-primary",
                  activeOverrides,
                )}
              >
                <Link
                  href={link.route}
                  aria-current={isCurrent ? "page" : undefined}
                >
                  {link.icon && (
                    <IconTile icon={link.icon} index={index} size="sm" />
                  )}
                  {link.label}
                </Link>
              </NavigationMenuLink>
            </li>
          );
        })}
      </ul>

      <div className="mt-3 grid grid-cols-[1fr_auto] items-center gap-x-3.5 gap-y-0.5 rounded-2xl promo-band py-2.5 pr-3 pl-4.5">
        <NavigationMenuLink
          asChild
          className="block p-0 text-[1.02rem] leading-tight font-extrabold text-ink hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary"
        >
          <Link href={panel.promo.route}>{panel.promo.title}</Link>
        </NavigationMenuLink>
        <NavigationMenuLink
          asChild
          className="block rounded-full bg-primary px-3.5 py-1.5 text-[13px] font-bold whitespace-nowrap text-white hover:bg-primary/90 hover:text-white focus:bg-primary/90 focus:text-white"
        >
          <Link href={panel.promo.cta.route}>{panel.promo.cta.label}</Link>
        </NavigationMenuLink>
        <span className="col-span-2 text-[12.5px] leading-snug font-medium text-ink-muted">
          {panel.promo.description}
        </span>
      </div>
    </div>
  </div>
);

export default ProductsMenuPanel;
